// Pure invariants governing project-scoped Voice_Profiles and Art_Style_Profiles
// (see design.md → "Profile Count Limit" and "Single-Default Enforcement";
// Correctness Properties 19 and 21; Req 10.1, 10.3, 10.5). This module is the
// single source of truth for two rules that resolvers and Lambdas must agree on:
//
//   1. A project may hold at most 20 profiles of each type. Creation is rejected
//      once the stored count reaches the limit (Property 19; Req 10.1, 10.3).
//   2. At most one profile of each type is the project default at any time.
//      Designating a new default clears the previous one (Property 21; Req 10.5).
//
// Both helpers are pure and total: they take plain data and return plain data
// with no I/O, so the persistence layer (a DynamoDB count read, a transactional
// write) can be injected around them while the decision stays testable in
// isolation.
/**
 * Maximum number of profiles of a single type (Voice_Profile or
 * Art_Style_Profile) permitted per project, inclusive. Req 10.1, 10.3.
 */
export const PROFILE_LIMIT = 20;
/**
 * Decide whether another profile of a given type may be created in a project,
 * given the count already stored for that type (Property 19; Req 10.1, 10.3).
 *
 * Returns `true` while the stored count is strictly below {@link PROFILE_LIMIT}
 * and `false` once it has reached the limit (a create at count 20 is rejected).
 *
 * The count is clamped defensively: a negative or non-integer input is coerced
 * so a malformed count can never spuriously permit a create beyond the limit.
 *
 * @param currentCount The number of profiles of the type already stored for the
 *   project.
 */
export function canCreateProfile(currentCount) {
    if (!Number.isFinite(currentCount)) {
        return false;
    }
    return currentCount < PROFILE_LIMIT;
}
/**
 * Apply a single-default designation across a list of same-type profiles
 * (Property 21; Req 10.5).
 *
 * Returns a new list in which `isDefault` is `false` on every profile except
 * the one whose id equals `targetId`, which is set to `true`. This enforces the
 * "at most one default per type" invariant: any previous default is cleared and
 * exactly the target becomes the default. If `targetId` matches no profile, the
 * result has every `isDefault` cleared (no default), which is the correct
 * outcome for an unknown or removed target.
 *
 * The function is pure: input profiles are not mutated. Each element is returned
 * either unchanged (when its `isDefault` already matches the desired value) or
 * as a shallow copy with only `isDefault` updated, preserving all other fields.
 * The relative order of profiles is preserved.
 *
 * The list is expected to contain profiles of a single type; the caller is
 * responsible for partitioning Voice_Profiles and Art_Style_Profiles before
 * calling, since the default invariant is enforced independently per type.
 *
 * @param profiles   The profiles of one type within a project.
 * @param targetId   The id of the profile to designate as the default.
 * @param idSelector Extracts the id from a profile (e.g. `p => p.voiceProfileId`
 *   or `p => p.artStyleProfileId`).
 */
export function applyDefaultDesignation(profiles, targetId, idSelector) {
    return profiles.map((profile) => {
        const shouldBeDefault = idSelector(profile) === targetId;
        if (profile.isDefault === shouldBeDefault) {
            return profile;
        }
        return { ...profile, isDefault: shouldBeDefault };
    });
}
//# sourceMappingURL=profiles.js.map